/**
 * ClassifyWidget — reusable 2-step classification UI (AC-3.6).
 *
 * Step 1: Category grid (matches V7 design — hTT59)
 * Step 2: Sub-type radio list (matches V7b design — CwM0I)
 *
 * Used by:
 *  - ClassifyFunnel (full-page classify flow, queues multiple files)
 *  - FileDetail (inline classify when a single file needs_classification)
 *
 * Props:
 *  - fileId           — the file to classify
 *  - filename         — shown in the document preview area
 *  - filePreviewUrl   — image URL for the preview (first_page_image_url)
 *  - fileIndex        — 1-based position (e.g. 1)
 *  - totalFiles       — total files in queue (e.g. 2), pass 1 to hide counter
 *  - onClassified     — called after a successful PATCH
 *  - onSkip           — called when user taps "Skip this file" (optional)
 *  - onRemove         — called when user taps "Remove this file" (optional)
 *  - onBack           — called when user taps the back arrow (optional)
 */
import React, { JSX, useCallback, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
// Category icons
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import BadgeIcon from '@mui/icons-material/Badge';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import InventoryIcon from '@mui/icons-material/Inventory';
import SellIcon from '@mui/icons-material/Sell';
import ArticleIcon from '@mui/icons-material/Article';
import GavelIcon from '@mui/icons-material/Gavel';
import HandshakeIcon from '@mui/icons-material/Handshake';
import RecentActorsIcon from '@mui/icons-material/RecentActors';
import PeopleIcon from '@mui/icons-material/People';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SchoolIcon from '@mui/icons-material/School';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import AccessibleIcon from '@mui/icons-material/Accessible';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useDocSchema } from 'src/hooks/aiFileUploadV2/useDocSchema';
import { getDocSubTypes } from 'src/services/aiFileUploadV2';
import axiosInstance from 'src/utils/axios';
import type {
  DocCategoryWithSubTypes,
  DocSubType,
} from 'src/types/aiFileUploadV2';
import DocumentPreviewBox from './DocumentPreviewBox';

// ─── Category icon map ────────────────────────────────────────────────────────

const CATEGORY_ICONS: Record<string, JSX.Element> = {
  pay_stub: <ReceiptLongIcon fontSize="small" />,
  self_employment_document: <BusinessCenterIcon fontSize="small" />,
  employment_verification: <BadgeIcon fontSize="small" />,
  benefits_letter: <CardGiftcardIcon fontSize="small" />,
  bank_statement: <AccountBalanceIcon fontSize="small" />,
  investment_statement: <ShowChartIcon fontSize="small" />,
  real_estate_document: <HomeWorkIcon fontSize="small" />,
  asset_statement: <InventoryIcon fontSize="small" />,
  asset_disposition_document: <SellIcon fontSize="small" />,
  tax_return: <ArticleIcon fontSize="small" />,
  court_order: <GavelIcon fontSize="small" />,
  support_agreement: <HandshakeIcon fontSize="small" />,
  government_id: <RecentActorsIcon fontSize="small" />,
  household_document: <PeopleIcon fontSize="small" />,
  lease_agreement: <VpnKeyIcon fontSize="small" />,
  residency_document: <LocationOnIcon fontSize="small" />,
  student_enrollment_letter: <SchoolIcon fontSize="small" />,
  deduction_document: <RemoveCircleOutlineIcon fontSize="small" />,
  mortgage_document: <CreditScoreIcon fontSize="small" />,
  disability_certification: <AccessibleIcon fontSize="small" />,
};

function getCategoryIcon(key: string): JSX.Element {
  return CATEGORY_ICONS[key] ?? <HelpOutlineIcon fontSize="small" />;
}

// ─── sx ──────────────────────────────────────────────────────────────────────

const sx = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    bgcolor: 'background.default',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: 2,
    py: 1.5,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: 600,
  },
  headerCounter: {
    fontSize: 13,
    color: 'text.secondary',
    fontWeight: 500,
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2.5,
    px: 2.5,
    pt: 3,
    pb: 4,
  },
  question: {
    fontSize: 18,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  categoryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 1.25,
  },
  categoryCard: {
    border: '1.5px solid',
    borderColor: 'divider',
    borderRadius: 2,
    p: 1.75,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    transition: 'border-color 0.15s, box-shadow 0.15s, background-color 0.15s',
    userSelect: 'none',
    '&:hover': {
      borderColor: 'primary.main',
      boxShadow: '0 0 0 1px',
      boxShadowColor: 'primary.main',
    },
  },
  categoryCardSelected: {
    borderColor: 'primary.main',
    bgcolor: 'primary.lighter',
    boxShadow: '0 0 0 1px',
  },
  categoryName: {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  categoryDesc: {
    fontSize: 11,
    color: 'text.secondary',
    lineHeight: 1.4,
  },
  subtypeList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  subtypeCard: {
    border: '1.5px solid',
    borderColor: 'divider',
    borderRadius: 2,
    px: 2,
    py: 1.25,
    cursor: 'pointer',
    transition: 'border-color 0.15s',
    '&:hover': {
      borderColor: 'primary.main',
    },
  },
  subtypeCardSelected: {
    borderColor: 'primary.main',
    bgcolor: 'primary.lighter',
  },
  spacer: {
    flex: 1,
  },
  skipRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    pt: 0.5,
  },
  skipText: {
    fontSize: 14,
    color: 'text.secondary',
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
  },
  separator: {
    fontSize: 14,
    color: 'divider',
  },
  removeText: {
    fontSize: 14,
    color: 'error.main',
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
  },
  confirmBtn: {
    height: 56,
    borderRadius: 4,
    fontSize: 15,
    fontWeight: 600,
  },
  loadingBox: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200,
  },
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ClassifyWidgetProps {
  fileId: number;
  filename: string;
  filePreviewUrl: string | null;
  processedFileUrl?: string | null;
  fileIndex?: number;
  totalFiles?: number;
  onClassified: (fileId: number) => void;
  onRemove?: (fileId: number) => void;
  onBack?: () => void;
}

type WidgetStep = 'category' | 'subtype';

// ─── ClassifyWidget ───────────────────────────────────────────────────────────

const ClassifyWidget = ({
  fileId,
  filename,
  filePreviewUrl,
  processedFileUrl,
  fileIndex,
  totalFiles,
  onClassified,
  onRemove,
  onBack,
}: ClassifyWidgetProps): JSX.Element => {
  const { categories, loading: schemaLoading } = useDocSchema();

  const [step, setStep] = useState<WidgetStep>('category');
  const [selectedCategory, setSelectedCategory] =
    useState<DocCategoryWithSubTypes | null>(null);
  const [selectedSubType, setSelectedSubType] = useState<DocSubType | null>(
    null
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Sub-types loaded on demand when user selects a category
  const [subTypes, setSubTypes] = useState<DocSubType[]>([]);
  const [subTypesLoading, setSubTypesLoading] = useState(false);
  // Cache fetched sub-types by category id to avoid re-fetching on back navigation
  const subTypesCache = useRef<Record<number, DocSubType[]>>({});

  const handleCategorySelect = useCallback(
    async (category: DocCategoryWithSubTypes) => {
      setSelectedCategory(category);
      setSelectedSubType(null);
      setStep('subtype');

      if (subTypesCache.current[category.id]) {
        setSubTypes(subTypesCache.current[category.id]);
        return;
      }
      setSubTypesLoading(true);
      try {
        const data = await getDocSubTypes(category.id);
        subTypesCache.current[category.id] = data;
        setSubTypes(data);
      } finally {
        setSubTypesLoading(false);
      }
    },
    []
  );

  const handleBackStep = useCallback(() => {
    if (step === 'subtype') {
      setStep('category');
      setSelectedSubType(null);
    } else if (onBack) {
      onBack();
    }
  }, [step, onBack]);

  const handleConfirm = useCallback(async () => {
    if (!selectedCategory || !selectedSubType) {
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await axiosInstance.patch(`v2/renter-hub/uploads/${fileId}/classify/`, {
        doc_category_id: selectedCategory.id,
        doc_sub_type_id: selectedSubType.id,
      });
      onClassified(fileId);
    } catch (err: unknown) {
      const e = err as Error;
      setSubmitError(e.message ?? 'Failed to classify file. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [selectedCategory, selectedSubType, fileId, onClassified]);

  const showCounter = fileIndex !== undefined && totalFiles !== undefined;
  const counterLabel = showCounter
    ? `File ${fileIndex} of ${totalFiles}`
    : undefined;

  const headerTitle =
    step === 'subtype' && selectedCategory
      ? selectedCategory.display_name
      : 'Help us label this file';

  const selectableCategories = categories.filter(
    (c) => c.is_user_selectable && c.key !== 'something_else'
  );

  // Loading state
  if (schemaLoading) {
    return (
      <Box sx={sx.page}>
        <Box sx={sx.header}>
          {onBack && (
            <IconButton size="small" onClick={onBack} aria-label="Back">
              <ArrowBackIcon />
            </IconButton>
          )}
        </Box>
        <Box sx={sx.loadingBox}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  // ── Step 1: Category ──────────────────────────────────────────────────────

  if (step === 'category') {
    return (
      <Box sx={sx.page} data-testid="classify-widget-category">
        {/* Header */}
        <Box sx={sx.header}>
          <Box sx={sx.headerLeft}>
            <IconButton
              size="small"
              onClick={handleBackStep}
              aria-label="Back"
              data-testid="classify-back-btn"
              sx={{ mr: -0.5 }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography sx={sx.headerTitle}>{headerTitle}</Typography>
          </Box>
          {counterLabel && (
            <Typography sx={sx.headerCounter}>{counterLabel}</Typography>
          )}
        </Box>

        {/* File preview */}
        <DocumentPreviewBox
          filename={filename}
          imageUrl={filePreviewUrl}
          processedFileUrl={processedFileUrl ?? null}
        />

        {/* Content */}
        <Box sx={sx.content}>
          <Typography sx={sx.question}>
            What category is this document?
          </Typography>

          {submitError && (
            <Typography
              color="error"
              fontSize={13}
              data-testid="classify-error"
            >
              {submitError}
            </Typography>
          )}

          <Box sx={sx.categoryGrid}>
            {selectableCategories.map((category) => (
              <Box
                key={category.id}
                sx={{
                  ...sx.categoryCard,
                  ...(selectedCategory?.id === category.id
                    ? sx.categoryCardSelected
                    : {}),
                }}
                onClick={() => handleCategorySelect(category)}
                data-testid={`classify-category-${category.key}`}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleCategorySelect(category);
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getCategoryIcon(category.key)}
                  <Typography sx={sx.categoryName}>
                    {category.display_name}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={sx.spacer} />

          {/* Remove action */}
          {onRemove && (
            <Box sx={sx.skipRow}>
              <Typography
                sx={sx.removeText}
                onClick={() => onRemove(fileId)}
                data-testid="classify-remove-btn"
              >
                Remove this file
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // ── Step 2: Sub-type ──────────────────────────────────────────────────────

  return (
    <Box sx={sx.page} data-testid="classify-widget-subtype">
      {/* Header */}
      <Box sx={sx.header}>
        <Box sx={sx.headerLeft}>
          <IconButton
            size="small"
            onClick={handleBackStep}
            aria-label="Back to categories"
            data-testid="classify-back-btn"
            sx={{ mr: -0.5 }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography sx={sx.headerTitle}>{headerTitle}</Typography>
        </Box>
        {counterLabel && (
          <Typography sx={sx.headerCounter}>{counterLabel}</Typography>
        )}
      </Box>

      {/* File preview */}
      <DocumentPreviewBox
        filename={filename}
        imageUrl={filePreviewUrl}
        processedFileUrl={processedFileUrl ?? null}
      />

      {/* Content */}
      <Box sx={sx.content}>
        <Typography sx={sx.question}>
          What type of {selectedCategory?.display_name ?? 'document'} is this?
        </Typography>

        {submitError && (
          <Typography color="error" fontSize={13} data-testid="classify-error">
            {submitError}
          </Typography>
        )}

        {subTypesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <RadioGroup
            value={selectedSubType?.id ?? ''}
            onChange={(e) => {
              const id = Number(e.target.value);
              setSelectedSubType(subTypes.find((s) => s.id === id) ?? null);
            }}
          >
            <Box sx={sx.subtypeList}>
              {subTypes.map((subType) => (
                <Box
                  key={subType.id}
                  sx={{
                    ...sx.subtypeCard,
                    ...(selectedSubType?.id === subType.id
                      ? sx.subtypeCardSelected
                      : {}),
                  }}
                  data-testid={`classify-subtype-${subType.key}`}
                >
                  <FormControlLabel
                    value={subType.id}
                    control={<Radio size="small" />}
                    label={subType.display_name}
                    sx={{ m: 0, width: '100%' }}
                  />
                </Box>
              ))}
            </Box>
          </RadioGroup>
        )}

        <Box sx={sx.spacer} />

        <Button
          variant="contained"
          fullWidth
          onClick={handleConfirm}
          disabled={!selectedSubType || submitting}
          data-testid="classify-confirm-btn"
          sx={sx.confirmBtn}
        >
          {submitting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress
                size={16}
                thickness={3}
                sx={{ color: 'inherit' }}
              />
              Saving&hellip;
            </Box>
          ) : (
            'Confirm'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default ClassifyWidget;
